import React from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Divider,
  Grid,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { CheckCircle, Star } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { useTranslation } from 'react-i18next';
import { translatePublicSite } from '../i18n/publicSiteText';
import { useNavigate } from 'react-router-dom';

interface PlanFeature {
  label: string;
  included: boolean;
}

const premiumFeatures: PlanFeature[] = [
  { label: 'Complete CMS workspace for client management', included: true },
  { label: 'Client profiles with linked charts and relations', included: true },
  { label: 'Planner, follow-ups, tasks and reminders', included: true },
  { label: 'Private notes, consultation history and report records', included: true },
  { label: 'PDF report downloads for saved client charts', included: true },
  { label: 'Unlimited chart saves for your workspace', included: true },
  { label: 'Nadi, KP and Vedic analysis', included: true },
  { label: 'KP Fruitful Significator Analyzer', included: true },
  { label: 'BNN, Jaimini and Western chart modules', included: true },
  { label: 'Vedic Numerology and Lo Shu Grid', included: true },
  { label: 'Daily Panchang and Ruling Planets', included: true },
  { label: 'Panch Pakshi, Lal Kitab and Transit tools', included: true },
  { label: 'Horary and Prashna chart tools', included: true },
];

const FEATURE_HI: Record<string, string> = {
  'Complete CMS workspace for client management': 'Client management के लिए पूरा CMS workspace',
  'Client profiles with linked charts and relations': 'Linked charts और relations के साथ client profiles',
  'Planner, follow-ups, tasks and reminders': 'Planner, follow-ups, tasks और reminders',
  'Private notes, consultation history and report records': 'Private notes, consultation history और report records',
  'PDF report downloads for saved client charts': 'Saved client charts के PDF report downloads',
  'Unlimited chart saves for your workspace': 'Workspace के लिए unlimited chart saves',
  'Nadi, KP and Vedic analysis': 'नाडी, KP और वैदिक analysis',
  'KP Fruitful Significator Analyzer': 'KP फलदायी संकेतक विश्लेषक',
  'BNN, Jaimini and Western chart modules': 'BNN, जैमिनी और Western chart modules',
  'Vedic Numerology and Lo Shu Grid': 'वैदिक Numerology और Lo Shu Grid',
  'Daily Panchang and Ruling Planets': 'Daily Panchang और Ruling Planets',
  'Panch Pakshi, Lal Kitab and Transit tools': 'Panch Pakshi, Lal Kitab और Transit tools',
  'Horary and Prashna chart tools': 'Horary और Prashna chart tools',
};

const premiumPlans = [
  {
    planType: 'premium' as const,
    title: 'Studio Pro Yearly',
    price: '₹999',
    duration: '/ year',
    badge: 'Best Value',
    description: 'Full-year access to all Studio Pro features.',
  },
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuth();
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const text = (en: string, hi?: string) => translatePublicSite(language, en, hi);
  const localFeature = (label: string) => text(label, FEATURE_HI[label]);

  const hasPremium = hasPremiumSubscription(state.user);
  const includedFromKundlistar = Boolean(
    hasPremium && state.user?.federation?.isShadow && state.user.federation.remoteProduct === 'kundlistar'
  );

  return (
    <Box sx={{ py: 8, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Chip
            label={includedFromKundlistar
              ? text('Included with Kundlistar Premium Pro', 'Kundlistar Premium Pro के साथ शामिल')
              : text('Simple Pricing', 'सरल मूल्य')}
            color="secondary"
            size="small"
            sx={{ mb: 2, fontWeight: 600 }}
          />
          <Typography variant="h3" fontWeight={600} gutterBottom>
            {text('Choose Your Plan', 'अपना प्लान चुनें')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 620, mx: 'auto' }}>
            {text('One ₹999 yearly Studio Pro plan for full access on web and in the app.', 'Web और app में पूरे access के लिए एक ₹999 वार्षिक Studio Pro plan।')}
          </Typography>
        </Box>

        {/* Plan Cards */}
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {premiumPlans.map((plan) => (
          <Grid key={plan.planType} size={{ xs: 12, sm: 10, md: 5 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '2px solid',
                borderColor: plan.planType === 'premium' ? 'primary.main' : 'divider',
                position: 'relative',
                boxShadow: '0 8px 32px rgba(46, 125, 82, 0.18)',
              }}
            >
              {/* Popular badge */}
              {plan.badge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <Chip
                  icon={<Star sx={{ fontSize: 14 }} />}
                  label={text('Most Popular', 'सबसे लोकप्रिय')}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600, px: 1 }}
                />
              </Box>
              )}

              <CardContent sx={{ flex: 1, p: 4 }}>
                <Typography variant="overline" color="primary" fontWeight={600}>
                  {text(plan.title, 'Studio Pro वार्षिक')}
                </Typography>
                {plan.planType === 'premium' && <Chip
                  label={text('Yearly Plan', 'वार्षिक प्लान')}
                  size="small"
                  color="secondary"
                  sx={{ mt: 1, mb: 1, fontWeight: 600 }}
                />}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, my: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="h3" fontWeight={600} color="primary.main">
                    {plan.price}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {text(plan.duration, '/ वर्ष')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {text(plan.description, 'सभी Studio Pro features का पूरे साल का access।')}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                  {premiumFeatures.map((f) => (
                    <FeatureRow key={f.label} label={localFeature(f.label)} included={f.included} />
                  ))}
                </Box>
              </CardContent>

              <Box sx={{ px: 4, pb: 4 }}>
                {hasPremium ? (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled
                  >
                    {text('Current Plan ✓', 'वर्तमान प्लान ✓')}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => navigate('/upgrade')}
                  >
                    {text('Upgrade to Studio Pro', 'Studio Pro में अपग्रेड करें')}
                  </Button>
                )}
              </Box>
            </Card>
          </Grid>
          ))}
        </Grid>

        {/* FAQ / note */}
        <Box
          sx={{
            mt: 8,
            p: 3,
            bgcolor: 'rgba(46, 125, 82, 0.05)',
            border: '1px solid',
            borderColor: 'rgba(46, 125, 82, 0.15)',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {text('Studio Pro web checkout is processed securely by Razorpay and activates on the same Studio account. For queries, reach us at', 'Studio Pro web checkout Razorpay से securely process होती है और उसी Studio account पर activate होती है। सहायता के लिए संपर्क करें')}{' '}
            <Typography
              component="a"
              href="mailto:info@kundlistarstudio.com"
              sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
            >
              info@kundlistarstudio.com
            </Typography>{' '}
            {text('or WhatsApp', 'या WhatsApp')}{' '}
            <Typography
              component="a"
              href="https://wa.me/918284927820"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
            >
              +91 8284927820
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

const FeatureRow: React.FC<PlanFeature> = ({ label, included }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <CheckCircle sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
    <Typography
      variant="body2"
      sx={{ color: included ? 'text.primary' : 'text.disabled', lineHeight: 1.6 }}
    >
      {label}
    </Typography>
  </Box>
);

export default PricingPage;
